import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { haptic } from '../lib/telegram';
import { t } from '../lib/i18n';
import { DAW_OPTIONS, DELIVERY_OPTIONS_BY_TYPE } from './types';

export interface FilterOptions {
  genre?: string;
  bpmMin?: number;
  bpmMax?: number;
  priceType?: 'all' | 'free' | 'paid';
  tags?: string[];
  daw?: string;
  deliveryOptions?: string[];
}

interface FilterDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterOptions;
  onApply: (filters: FilterOptions) => void;
  category?: string;
}

const GENRES = [
  'Hip-Hop', 'Trap', 'R&B', 'Pop', 'Rock', 'Electronic', 
  'Lo-Fi', 'Drill', 'Reggaeton', 'Afrobeat', 'House'
];

const DESIGN_TAGS = [
  'Minimalist', 'Abstract', 'Typography', 'Illustration', 'Photography', '3D', 'Vintage', 'Modern'
];

const MIXING_TAGS = [
  'Mixing', 'Mastering', 'Vocal Tuning', 'Stem Mixing'
];

const KIT_TAGS = [
  'Drum Kit', 'Loop Kit', 'MIDI Kit', 'Preset Bank', 'One Shot'
];

const SERVICE_TAGS = [
  'Songwriting', 'Production', 'Session Musician', 'Promotion', 'Artwork'
];

export function FilterDropdown({ isOpen, onClose, filters, onApply, category = 'all' }: FilterDropdownProps) {
  const [localFilters, setLocalFilters] = useState<FilterOptions>(filters);
  const translations = t();

  // Reset local filters when opening with new props
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);

  if (!isOpen) return null;

  const handleApply = () => {
    haptic.medium();
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    haptic.light();
    const resetFilters: FilterOptions = {};
    setLocalFilters(resetFilters);
    onApply(resetFilters);
    onClose();
  };

  const getTags = () => {
    switch (category) {
      case 'covers': return DESIGN_TAGS;
      case 'mixing': return MIXING_TAGS;
      case 'kits': return KIT_TAGS;
      case 'services': return SERVICE_TAGS;
      case 'beats':
      case 'features':
      case 'all':
      default:
        return GENRES;
    }
  };

  const getTagLabel = () => {
    switch (category) {
      case 'mixing': return 'Тип послуги';
      case 'kits': return 'Тип кіта';
      case 'services': return 'Тип послуги';
      case 'covers': return 'Стиль дизайну';
      default: return 'Жанр';
    }
  };

  const showBpm = ['beats', 'features', 'all', 'beat', 'feature'].includes(category);
  const currentTags = getTags();
  const tagLabel = getTagLabel();

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-t-3xl glass backdrop-blur-2xl border-t border-white/10 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 glass backdrop-blur-2xl border-b border-white/10">
          <h3 className="text-xl font-bold text-white">{translations.common.filter}</h3>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center glass border border-white/20 hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Dynamic Tags Filter */}
          <div>
            <label className="block text-sm font-semibold text-white/70 mb-3 uppercase">
              {tagLabel}
            </label>
            <div className="flex flex-wrap gap-2">
              {currentTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => {
                    haptic.light();
                    setLocalFilters({
                      ...localFilters,
                      genre: localFilters.genre === tag ? undefined : tag,
                    });
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    localFilters.genre === tag
                      ? 'bg-splyt-green text-splyt-dark border border-splyt-green'
                      : 'glass border border-white/10 text-white/70 hover:border-white/30'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* BPM Range - Only for beats/features/all */}
          {showBpm && (
            <div>
              <label className="block text-sm font-semibold text-white/70 mb-3 uppercase">
                BPM ({localFilters.bpmMin || 60} - {localFilters.bpmMax || 200})
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-white/50 w-16">Min:</span>
                  <input
                    type="number"
                    min="60"
                    max="200"
                    value={localFilters.bpmMin || 60}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 60;
                      setLocalFilters({
                        ...localFilters,
                        bpmMin: value,
                        bpmMax: localFilters.bpmMax || 200,
                      });
                    }}
                    className="flex-1 h-10 px-4 rounded-xl glass border border-white/10 text-white bg-white/5 focus:border-splyt-green/50 focus:outline-none"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-white/50 w-16">Max:</span>
                  <input
                    type="number"
                    min="60"
                    max="200"
                    value={localFilters.bpmMax || 200}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 200;
                      setLocalFilters({
                        ...localFilters,
                        bpmMax: value,
                        bpmMin: localFilters.bpmMin || 60,
                      });
                    }}
                    className="flex-1 h-10 px-4 rounded-xl glass border border-white/10 text-white bg-white/5 focus:border-splyt-green/50 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Price Type */}
          <div>
            <label className="block text-sm font-semibold text-white/70 mb-3 uppercase">
              Ціна
            </label>
            <div className="flex gap-2">
              {(['all', 'free', 'paid'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    haptic.light();
                    setLocalFilters({
                      ...localFilters,
                      priceType: type,
                    });
                  }}
                  className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    localFilters.priceType === type
                      ? 'bg-splyt-green text-splyt-dark border border-splyt-green'
                      : 'glass border border-white/10 text-white/70 hover:border-white/30'
                  }`}
                >
                  {type === 'all' ? 'Всі' : type === 'free' ? 'Безкоштовні' : 'Платні'}
                </button>
              ))}
            </div>
          </div>

          {/* DAW Filter */}
          {category === 'beats' && (
            <div>
              <label className="block text-sm font-semibold text-white/70 mb-3 uppercase">
                DAW
              </label>
              <div className="flex flex-wrap gap-2">
                {DAW_OPTIONS.map((daw) => (
                  <button
                    key={daw}
                    onClick={() => {
                      haptic.light();
                      setLocalFilters({
                        ...localFilters,
                        daw: localFilters.daw === daw ? undefined : daw,
                      });
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      localFilters.daw === daw
                        ? 'bg-splyt-green text-splyt-dark border border-splyt-green'
                        : 'glass border border-white/10 text-white/70 hover:border-white/30'
                    }`}
                  >
                    {daw}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Delivery Options Filter */}
          {category === 'beats' && (
            <div>
              <label className="block text-sm font-semibold text-white/70 mb-3 uppercase">
                Що включено
              </label>
              <div className="flex flex-wrap gap-2">
                {DELIVERY_OPTIONS_BY_TYPE.beat.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      haptic.light();
                      const currentDeliveryOptions = localFilters.deliveryOptions || [];
                      const newDeliveryOptions = currentDeliveryOptions.includes(option.id)
                        ? currentDeliveryOptions.filter((id) => id !== option.id)
                        : [...currentDeliveryOptions, option.id];
                      setLocalFilters({
                        ...localFilters,
                        deliveryOptions: newDeliveryOptions,
                      });
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      localFilters.deliveryOptions?.includes(option.id)
                        ? 'bg-splyt-green text-splyt-dark border border-splyt-green'
                        : 'glass border border-white/10 text-white/70 hover:border-white/30'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 left-0 right-0 p-4 glass backdrop-blur-2xl border-t border-white/10">
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 h-12 rounded-xl glass border border-white/10 text-white font-medium hover:bg-white/10 transition-all"
            >
              Скинути
            </button>
            <button
              onClick={handleApply}
              className="flex-1 h-12 rounded-xl bg-splyt-green text-splyt-dark font-bold glow-green hover:scale-105 transition-all"
            >
              Застосувати
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
